import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import Quill from 'quill/core';
import React, { useLayoutEffect, useState } from 'react';
import * as styles from './EditorPlaceholder.module.scss';
import { useQuill } from './QuillContext';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import LoadingIndicator from '../components/LoadingIndicator';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import googleDriveIcon from '../icons/non-prefilled/16x16/googleDrive.svg';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import templateIcon from '../icons/non-prefilled/16x16/template.svg';
import uploadIcon from '../icons/non-prefilled/16x16/upload.svg';
import { useAddTopicToPost } from '../post/sharing/postPermissionOperations';
import type { SlimPostWithTopics } from '../post/useAllSlimPosts';
import useDeletePost from '../post/useDeletePost';
import GoogleDrivePicker from '../search/GoogleDrivePicker';
import type { LibraryTemplate } from '../templates/useTemplatesSections';
import useTemplatesSections from '../templates/useTemplatesSections';
import { UIActions } from '../ui/uiSlice';
import useNavigate from '../utils/router/useNavigate';
import truncate from '../utils/text/truncate';
import postPath, { createPostPath } from '../utils/url/postPath';
import useIsPublicView from '../utils/useIsPublicView';

const EDITOR_PLACEHOLDER_POST_FRAGMENT = gql(`
  fragment EditorPlaceholder_post on PostFields {
    id
    topics {
      id
    }
  }
`);

const IMPORT_FILES_MUTATION = gql(/** @complexity 8 */ `
  mutation EditorPlaceholderImportFiles($files: [Upload]!) {
    importFiles(files: $files, isDraft: true) {
      post {
        id
        publishedAt
        title
      }
    }
  }
`);

interface EditorPlaceholderProps {
  post: DocumentType<typeof EDITOR_PLACEHOLDER_POST_FRAGMENT>;
}

const EditorPlaceholder = ({ post }: EditorPlaceholderProps) => {
  const { quill } = useQuill();
  const dispatch = useAppDispatch();
  const [isHidden, setIsHidden] = useState(false);
  const isPublicView = useIsPublicView();

  const [marginTop, setMarginTop] = useState(0);

  useLayoutEffect(() => {
    if (!quill) return undefined;
    const { firstChild } = quill.root;
    if (!(firstChild instanceof HTMLElement)) return undefined;

    const observer = new ResizeObserver(() => {
      const rootRect = quill.root.getBoundingClientRect();
      const firstChildRect = firstChild.getBoundingClientRect();
      const marginBottom = parseInt(
        getComputedStyle(firstChild).marginBottom,
        10,
      );
      const newMarginTop =
        firstChildRect.bottom - rootRect.bottom + marginBottom;
      setMarginTop(newMarginTop);
    });

    observer.observe(firstChild);

    return () => {
      observer.disconnect();
    };
  }, [quill]);

  const navigate = useNavigate();

  const deletePost = useDeletePost();
  const [importFiles] = useMutation(IMPORT_FILES_MUTATION);

  const addTopic = useAddTopicToPost();
  const copyTopics = async (targetPostId: string) => {
    const topicIds = post.topics.map((topic) => topic.id);
    await Promise.all(
      topicIds.map((topicId) => addTopic(targetPostId, topicId)),
    );
  };

  const templatesSections = useTemplatesSections({
    totalLimit: 6,
    skip: isPublicView,
  });
  const templates = templatesSections.flatMap<
    SlimPostWithTopics | LibraryTemplate
  >((section) => section.templates);

  if (isHidden || !quill) {
    return null;
  }

  const startLoading = () => {
    dispatch(
      UIActions.openModal(() => <LoadingIndicator delay={0} card />, {
        disableOutsideClose: true,
      }),
    );
  };

  const stopLoading = () => {
    dispatch(UIActions.closeModal());
  };

  const deletePostWithLoading = async () => {
    startLoading();
    try {
      await deletePost(post.id);
    } finally {
      stopLoading();
    }
  };

  return (
    <div data-no-print className={styles.container} style={{ marginTop }}>
      <div className={styles.content}>
        <div className={styles.note}>
          {isPublicView
            ? 'Start writing here or type / to show all formatting options'
            : 'Start typing or select a quick start option below'}
        </div>
        {!isPublicView && (
          <>
            <div className={styles.actions}>
              <div className={styles.title}>Starting point</div>
              <Button
                className={styles.button}
                onClick={() => {
                  setIsHidden(true);

                  let targetIndex = 0;
                  const [firstLine] = quill.getLine(0);
                  if (!firstLine) return;
                  if (!quill.noTitleLine) {
                    targetIndex =
                      firstLine.offset(quill.scroll) + firstLine.length();
                  }
                  quill.focusWithoutScrolling();
                  quill.setSelection(targetIndex, 0, Quill.sources.SILENT);
                }}
              >
                <SVGIcon className={styles.icon}>{postIcon}</SVGIcon>
                Start from scratch
              </Button>
              <Button className={styles.button} id="aria-upload-a-file-button">
                <SVGIcon className={styles.icon}>{uploadIcon}</SVGIcon>
                <label className={styles.fileInputLabel}>
                  <input
                    type="file"
                    accept=".md,.docx,.html,.htm"
                    className={styles.fileInput}
                    onChange={async (e) => {
                      const file = e.currentTarget.files?.[0];
                      if (!file) return;

                      startLoading();
                      try {
                        const { data } = await importFiles({
                          variables: { files: [file] },
                        });
                        const importedPost = data?.importFiles[0]?.post;
                        if (importedPost) {
                          await copyTopics(importedPost.id);
                          navigate(postPath(importedPost), { replace: true });
                          await deletePost(post.id);
                        }
                      } finally {
                        stopLoading();
                      }
                    }}
                    aria-labelledby="aria-upload-a-file-button"
                  />
                </label>
                Upload a file
              </Button>
              <Button
                className={styles.button}
                onClick={() => {
                  dispatch(
                    UIActions.openModal(GoogleDrivePicker, {
                      disableOutsideClose: true,
                      navigationMethod: 'replace',
                      onBeforeNavigate: deletePostWithLoading,
                      topicIds: post.topics.map((topic) => topic.id),
                    }),
                  );
                }}
              >
                <SVGIcon className={styles.icon}>{googleDriveIcon}</SVGIcon>
                Import from Google
              </Button>
            </div>
            <div
              className={classNames(styles.actions, styles.templatesActions)}
            >
              <div className={styles.title}>Suggested templates</div>
              <div className={styles.templatesList}>
                {templates.map((template) => (
                  <Button
                    key={'id' in template ? template.id : template.libraryId}
                    className={classNames(styles.button, styles.template)}
                    onClick={() => {
                      navigate(
                        'id' in template
                          ? createPostPath({ templateId: template.id })
                          : createPostPath({ library: template.libraryId }),
                        { replace: true },
                      );
                      return deletePostWithLoading();
                    }}
                  >
                    <SVGIcon className={styles.icon}>{templateIcon}</SVGIcon>
                    {'libraryId' in template
                      ? template.title
                      : // Truncate user templates title if it's too long
                        truncate(template.title, 50)}
                  </Button>
                ))}
                <Button
                  className={classNames(styles.button, styles.template)}
                  to="/templates"
                  target="_blank"
                >
                  <SVGIcon className={styles.icon}>{overflowMenuIcon}</SVGIcon>
                  See all templates
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditorPlaceholder;
